import { Link } from 'react-router-dom';
import { Youtube, Instagram, Facebook, Music, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { AdUnit } from './AdUnit';

export function Footer() {
  const currentYear = new Date().getFullYear();

  const sectionVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1 },
    }),
  };

  const socialLinks = [
    {
      name: 'YouTube',
      icon: Youtube,
      href: 'https://www.youtube.com/@radarjovem_mz',
      gradient: 'from-red-600 to-red-700',
      hoverColor: 'hover:shadow-red-500/50',
    },
    {
      name: 'Instagram',
      icon: Instagram,
      href: 'https://www.instagram.com/radar.jovem?igsh=MWN6YWtpeGozZzF4eg==',
      gradient: 'from-pink-600 to-pink-700',
      hoverColor: 'hover:shadow-pink-500/50',
    },
    {
      name: 'TikTok',
      icon: Music,
      href: 'https://www.tiktok.com/@radar.jovem?_r=1&_t=ZS-95Ti7COXPGP',
      gradient: 'from-black to-gray-800',
      hoverColor: 'hover:shadow-black/50',
    },
    {
      name: 'Facebook',
      icon: Facebook,
      href: 'https://www.facebook.com/radarjovem.mz',
      gradient: 'from-blue-600 to-blue-700',
      hoverColor: 'hover:shadow-blue-500/50',
    },
  ];

  const navLinks = [
    { label: 'Notícias', href: '/' },
    { label: 'Categorias', href: '/?category=Política' },
    { label: 'Sobre', href: '/about' },
    { label: 'Contato', href: '/contact' },
  ];

  const categoryLinks = [
    { label: 'Política', href: '/?category=Política' },
    { label: 'Economia', href: '/?category=Economia' },
    { label: 'Desporto', href: '/?category=Desporto' },
    { label: 'Turismo', href: '/?category=Turismo' },
    { label: 'Internacional', href: '/?category=Internacional' },
    { label: 'Cultura', href: '/?category=Cultura' },
    { label: 'Saúde', href: '/?category=Saúde' },
  ];

  const footerLinks = [
    { label: 'Privacidade', href: '/privacy' },
    { label: 'Termos', href: '/terms' },
    { label: 'Contato', href: '/' },
  ];

  return (
    <footer className="relative mt-20 bg-gradient-to-b from-dark-900/50 via-dark-950 to-dark-950 dark:from-dark-900 dark:via-dark-950 dark:to-black border-t border-white/10 dark:border-primary-900/20">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-accent-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-16 md:py-20">
        {/* Main Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Brand Section */}
          <motion.div
            custom={0}
            variants={sectionVariants}
            className="lg:col-span-1"
          >
            <motion.div
              className="mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <img src="/logo.png" alt="Radar Jovem" loading="lazy" decoding="async" className="h-12 rounded-lg shadow-elevation" />
            </motion.div>
            <p className="text-gray-400 text-sm leading-relaxed font-light">
              As notícias mais relevantes de Moçambique e do mundo
            </p>
          </motion.div>

          {/* Navigation */}
          <motion.div
            custom={1}
            variants={sectionVariants}
          >
            <h4 className="font-serif font-bold text-white mb-6 text-lg flex items-center gap-2">
              <span className="w-1 h-1 bg-gradient-orange rounded-full" />
              Navegação
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center gap-2 group text-sm"
                  >
                    <span className="w-0 h-px bg-gradient-orange group-hover:w-2 transition-all duration-200" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Categorias */}
          <motion.div
            custom={2}
            variants={sectionVariants}
          >
            <h4 className="font-serif font-bold text-white mb-6 text-lg flex items-center gap-2">
              <span className="w-1 h-1 bg-gradient-orange rounded-full" />
              Categorias
            </h4>
            <ul className="space-y-3">
              {categoryLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center gap-2 group text-sm"
                  >
                    <span className="w-0 h-px bg-gradient-orange group-hover:w-2 transition-all duration-200" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Redes Sociais */}
          <motion.div
            custom={3}
            variants={sectionVariants}
          >
            <h4 className="font-serif font-bold text-white mb-6 text-lg flex items-center gap-2">
              <span className="w-1 h-1 bg-gradient-orange rounded-full" />
              Redes Sociais
            </h4>
            <div className="space-y-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-gradient-to-r ${social.gradient} text-white hover:shadow-lg hover:shadow-current transition-all duration-300 group`}
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    title={social.name}
                  >
                    <Icon size={18} />
                    <span className="text-sm font-semibold">{social.name}</span>
                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

        </motion.div>

        <AdUnit placement="footer" compact className="mb-12" />

        {/* Divider */}
        <motion.div
          className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          viewport={{ once: true }}
        />

        {/* Bottom Section */}
        <motion.div
          className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between text-sm"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Copyright */}
          <p className="text-gray-500">
            &copy; {currentYear} <span className="text-transparent bg-clip-text bg-gradient-text font-semibold">Radar Jovem</span>. Todos os direitos reservados.
          </p>

          {/* Links */}
          <div className="flex gap-6 md:gap-8">
            {footerLinks.map((link) => (
              <motion.a
                key={link.href}
                href={link.href}
                className="text-gray-500 hover:text-white transition-colors duration-200 font-medium"
                whileHover={{ y: -2 }}
              >
                {link.label}
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom gradient accent */}
      <div className="h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />
    </footer>
  );
}
